import { useMemo, useEffect, useState } from 'react';
import { Firestore } from 'firebase/firestore';
import { MessagingService } from '@/services/messagingService';
import { useMessaging } from '@/hooks/useMessaging';
import { UserProfile } from '@/types/user';
import { ConversationList } from './ConversationList';
import { ConversationView } from './ConversationView';
import { useSharedProfile } from '@/hooks/useSharedProfile';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from "@/hooks/use-toast";
import { Toaster } from "@/components/ui/toaster";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

interface MessagingLayoutProps {
  currentUser: UserProfile;
  db: Firestore;
  initialConversationId?: string;
  onMessageReceived?: (message: any) => any;
}

// Match ConversationView's expected type exactly
type OtherUserType = {
  id: string;
  name: string;
  imageUrl?: string;
  userType?: 'talent' | 'promoter';
} | undefined;

export function MessagingLayout({
  currentUser,
  db,
  initialConversationId,
  onMessageReceived
}: MessagingLayoutProps) {
  const messagingService = useMemo(() => new MessagingService(db), [db]);
  
  const {
    conversations,
    messages,
    currentConversation,
    setCurrentConversation,
    sendMessage,
    isLoadingMessages,
    isLoadingConversations,
    loadMoreMessages,
    messageErrors,
    clearMessageError,
    hasMoreMessages,
    deleteConversation,
    unreadCount,
    handleReportConversation,
  } = useMessaging({
    currentUser,
    messagingService
  });

  const queryClient = useQueryClient();
  const { toast } = useToast();

  useEffect(() => {
    if (initialConversationId) {
      // Wait for conversations to load before setting current conversation
      if (!isLoadingConversations) {
        const exists = conversations.find(conv => conv.id === initialConversationId);
        if (exists) {
          setCurrentConversation(initialConversationId);
        } else {
          // If conversation doesn't exist in the list yet, trigger a refetch
          queryClient.refetchQueries({
            queryKey: ['conversations', currentUser.id]
          });
        }
      }
    }
  }, [initialConversationId, setCurrentConversation, conversations, isLoadingConversations, queryClient, currentUser.id]);

  // Remove debug logging effects and statements
  const currentConversationData = useMemo(() => {
    if (!currentConversation || isLoadingConversations) {
      return undefined;
    }
    const conv = conversations.find(conv => conv.id === currentConversation);
    return conv;
  }, [currentConversation, conversations, isLoadingConversations]);

  // Remove debug log for other user ID
  const otherUserId = useMemo(() => {
    if (!currentConversationData?.otherUser) return undefined;
    return currentConversationData.otherUser.id;
  }, [currentConversationData]);

  // Get profile using shared hook
  const { data: otherUserProfile } = useSharedProfile(otherUserId, 'messaging');

  // Combine conversation data with profile
  const enrichedOtherUser: OtherUserType = useMemo(() => {
    if (!currentConversationData?.otherUser) return undefined;

    return {
      id: otherUserId!,
      name: currentConversationData.otherUser.name,
      imageUrl: currentConversationData.otherUser.imageUrl,
      userType: otherUserProfile?.type,
      ...(otherUserProfile && {
        name: otherUserProfile.type === 'talent' 
          ? otherUserProfile.talentData?.name ?? currentConversationData.otherUser.name
          : otherUserProfile.promoterData?.promotionName ?? currentConversationData.otherUser.name,
        imageUrl: otherUserProfile.type === 'talent'
          ? otherUserProfile.talentData?.imageUrl
          : otherUserProfile.promoterData?.imageUrl
      })
    };
  }, [currentConversationData, otherUserProfile, otherUserId]);

  // Add state for mobile view
  const [isMobileView, setIsMobileView] = useState(false);

  // Update isMobileView on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768); // 768px is typical tablet/mobile breakpoint
    };

    // Set initial value
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [conversationToDelete, setConversationToDelete] = useState<string | null>(null);

  const handleDeleteConversation = async (conversationId: string) => {
    setConversationToDelete(conversationId);
  };

  const confirmDelete = async () => {
    if (conversationToDelete) {
      try {
        await deleteConversation(conversationToDelete);
        toast({
          title: "Conversation deleted",
          description: "The conversation has been removed from your inbox.",
        });
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to delete conversation. Please try again.",
          variant: "destructive",
        });
      }
      setConversationToDelete(null);
    }
  };

  const handleToggleUnread = async (conversationId: string) => {
    try {
      await messagingService.toggleUnread(conversationId, currentUser.id);
      queryClient.invalidateQueries({
        queryKey: ["unreadCount", currentUser.id],
      });
      toast({
        title: "Updated",
        description: "Conversation marked as unread",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update conversation. Please try again.",
        variant: "destructive",
      });
    }
  };

  const [conversationToReport, setConversationToReport] = useState<{
    conversationId: string;
    reportedUserId: string;
  } | null>(null);

  const handleReportClick = (conversationId: string, reportedUserId: string) => {
    setConversationToReport({ conversationId, reportedUserId });
  };

  const confirmReport = async () => {
    if (conversationToReport) {
      try {
        await handleReportConversation(
          conversationToReport.conversationId,
          conversationToReport.reportedUserId
        );
      } catch (error) {
        console.error('Error confirming report:', error);
      }
      setConversationToReport(null);
    }
  };

  // Add this where messages are processed
  const processedMessages = messages?.map(msg => 
    onMessageReceived ? onMessageReceived(msg) : msg
  );

  return (
    <>
      <div className="flex h-screen bg-black">
        {/* Conversations List */}
        <div 
          className={`${
            isMobileView ? 'w-full' : 'w-1/3'
          } border-r border-gray-800 ${
            isMobileView && currentConversation ? 'hidden' : 'block'
          } bg-gray-900`}
        >
          <div className="p-4 border-b border-gray-800">
            <h2 className="text-lg font-semibold text-white">Messages</h2>
          </div>
          <ConversationList
            conversations={conversations}
            currentConversation={currentConversation}
            onSelectConversation={setCurrentConversation}
            onDeleteConversation={(id) => setConversationToDelete(id)}
            onReportConversation={handleReportClick}
            isLoading={isLoadingConversations}
          />
        </div>
        
        {/* Main Conversation Area */}
        <div 
          className={`${
            isMobileView ? 'w-full' : 'w-2/3'
          } ${
            isMobileView && !currentConversation ? 'hidden' : 'block'
          } bg-gray-900`}
        >
          {currentConversation ? (
            <>
              {isMobileView && (
                <div className="border-b border-gray-800 p-2 bg-gray-900">
                  <Button
                    variant="ghost"
                    onClick={() => setCurrentConversation(null)}
                    className="flex items-center text-gray-100 hover:text-white hover:bg-gray-800"
                  >
                    <ArrowLeft className="h-5 w-5 mr-2" />
                    Back to Messages
                  </Button>
                </div>
              )}
              <ConversationView
                messages={processedMessages}
                currentUser={currentUser}
                onSendMessage={sendMessage}
                isLoading={isLoadingMessages}
                onLoadMore={loadMoreMessages}
                conversationId={currentConversation}
                messageError={messageErrors[currentConversation]}
                onClearError={() => clearMessageError(currentConversation)}
                hasMore={hasMoreMessages}
                otherUser={enrichedOtherUser}
              />
            </>
          ) : (
            <div className="h-full flex items-center justify-center">
              {isLoadingConversations ? (
                <div className="text-gray-100">Loading...</div>
              ) : conversations.length === 0 ? (
                <div className="text-center">
                  <h3 className="text-lg font-medium mb-2 text-gray-100">No Messages Yet</h3>
                  <p className="text-sm text-gray-300">When you message someone, the conversation will appear here</p>
                </div>
              ) : (
                <div className="text-center">
                  <h3 className="text-lg font-medium mb-2 text-gray-100">Select a Conversation</h3>
                  <p className="text-sm text-gray-300">Choose a conversation from the list to start chatting</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <Toaster />
      <AlertDialog 
        open={!!conversationToDelete} 
        onOpenChange={(open) => !open && setConversationToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Conversation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this conversation? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 hover:bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog 
        open={!!conversationToReport} 
        onOpenChange={(open) => !open && setConversationToReport(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Report Conversation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to report this conversation? This will notify our moderation team.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmReport} className="bg-red-500 hover:bg-red-600">
              Report
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}